import React, { useState } from "react";
import toast from "react-hot-toast";
import ConfirmModal from "./ConfirmModal";
import WithdrawPropertyModal from "./WithdrawPropertyModal";
import useWithdrawProperty from "../../hooks/useWithdrawProperty";

/**
 * Owns the Withdraw/Relist reconfirm dialogs and their loading state, shared
 * across the property detail pages. Withdrawing opens the reason-picker
 * dialog (see WithdrawPropertyModal); relisting keeps the plain reconfirm
 * since it needs no reason. Callers supply the two async actions, which
 * differ per page (plain CRM Property vs. RealEstateProperty, with or
 * without a linked REA listing), and a render-prop for the trigger button
 * so each page keeps its own button styling.
 */
const PropertyWithdrawControls = ({ property, onWithdraw, onRelist, children }) => {
    const [isRelisting, setIsRelisting] = useState(false);
    const [showRelistModal, setShowRelistModal] = useState(false);
    const {
        isWithdrawModalOpen,
        withdrawStatusName,
        isWithdrawing,
        openWithdraw,
        closeWithdraw,
        confirmWithdraw,
    } = useWithdrawProperty();

    const handleClick = () => {
        if (property?.is_withdrawn) {
            setShowRelistModal(true);
            return;
        }
        const statusName = property?.status?.name || property?.status || "";
        openWithdraw(statusName, onWithdraw);
    };

    const handleRelist = async () => {
        setIsRelisting(true);
        try {
            await onRelist();
            setShowRelistModal(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update withdrawn status");
            console.error("Relist error:", err);
        } finally {
            setIsRelisting(false);
        }
    };

    return (
        <>
            {children({ onClick: handleClick, loading: isWithdrawing || isRelisting })}
            <ConfirmModal
                isOpen={showRelistModal}
                onClose={() => setShowRelistModal(false)}
                onConfirm={handleRelist}
                loading={isRelisting}
                title="Relist Property"
                description="Are you sure you want to relist this property? It will be visible in the main listings again."
                confirmText="Relist"
                variant="primary"
            />
            <WithdrawPropertyModal
                isOpen={isWithdrawModalOpen}
                onClose={closeWithdraw}
                onConfirm={confirmWithdraw}
                loading={isWithdrawing}
                statusName={withdrawStatusName}
            />
        </>
    );
};

export default PropertyWithdrawControls;
